import Phaser from "phaser";
import { detectPlatform } from "@/platform/detectPlatform";
import { GameConfig } from "@/config/GameConfig";
import { createButton } from "@/ui/Button";
import { contentFlowStartY, layoutButtonRows } from "@/ui/buttonLayout";
import { hexToCss } from "@/ui/colors";
import { ScoreChallengeClient } from "@/daily/ScoreChallengeClient";
import { DailyChallengeClient } from "@/daily/DailyChallengeClient";
import { compareToTarget, describeComparison, formatEndsIn, truncateDisplayName, attemptsRemainingBucket } from "@/daily/scoreChallenge";
import { isPlausibleChallengeToken } from "@/daily/challengeRoute";
import { resolveScoreChallengeLookup } from "@/daily/scoreChallengeLookup";
import { createShareChallengeModal, type ShareChallengeModalHandle } from "@/ui/ShareChallengeModal";
import { ScoreChallengeSession } from "@/daily/scoreChallengeSession";
import { resolveChallengeBaseUrl, buildChallengeUrl, isSharingEnabled } from "@/daily/shareUrl";
import { trackScoreChallengeCtaClicked, trackScoreChallengeOpened, trackScoreSharePromptViewed } from "@/analytics/gameAnalytics";
import { buildScoreChallengeQaFixture, isScoreChallengeQaAllowedHost, type ScoreChallengeQaPresetId } from "@/daily/scoreChallengeQa";
import type { ScoreComparisonResult } from "@/daily/scoreChallenge";
import { attachOrientationGate, type OrientationGateHandle } from "@/scenes/OrientationGate";

/**
 * `"unavailable"` covers every lookup-infrastructure failure - the
 * `get_score_challenge` RPC not existing yet (migration `006` not
 * applied), a network rejection, a malformed/unexpected response, or any
 * other unhandled exception anywhere in `loadChallenge()` - see
 * `resolveScoreChallengeLookup`'s doc. It renders identically to
 * `"invalid"` (`CHALLENGE UNAVAILABLE` / `But today's Daily is ready.` /
 * `PLAY TODAY'S DAILY`) - kept as its own state only so a genuinely
 * invalid token and an infrastructure failure can be told apart in tests/
 * telemetry, never so a visitor sees a different, worse experience for one
 * versus the other.
 */
type LoadState = "loading" | "invalid" | "expired" | "self" | "resolved" | "unavailable";

interface ResolvedState {
  challengeId: string;
  targetDailyPoints: number;
  creatorDisplayName: string;
  analyticsId: string;
  expiresAtMs: number | null;
  attemptsRemaining: number;
  hasResult: boolean;
  dailyPoints: number | null;
  comparison: ScoreComparisonResult | null;
}

/**
 * The "Beat My Score" share-link landing card: `/c/<public_id>` routes
 * here (see `MenuScene.create()`'s one-shot redirect). Never duplicates
 * Daily state or game logic - the actual attempt reservation/gameplay/
 * result submission all still happen in the existing
 * `DailyChallengeScene` -> `GameScene` -> `DailyResultScene` chain; this
 * scene's only job is (a) show the frozen target, (b) inspect the
 * visitor's OWN existing verified Daily state via the same
 * `DailyChallengeClient` reads every other Daily screen already uses, and
 * (c) hand off into that same normal flow with a `sourceChallenge` context
 * attached for display/comparison.
 */
export class ScoreChallengeScene extends Phaser.Scene {
  private token = "";
  private loadState: LoadState = "loading";
  private resolved: ResolvedState | null = null;
  private isDisposed = false;
  private isCreatingChallenge = false;
  private shareCreateError = "";
  /** True only for a QA fixture preview (`data.qaPreset`, host-gated) - never for a real token, however it resolves. See `src/daily/scoreChallengeQa.ts`. When true, `load()`/`checkRollover()`/analytics/attribution are all skipped entirely - this scene renders straight from the synthetic fixture and never touches the network. */
  private isQaFixture = false;
  private mainContainer!: Phaser.GameObjects.Container;
  private shareModal!: ShareChallengeModalHandle;
  private expiryCheckEvent?: Phaser.Time.TimerEvent;
  /** PR31 follow-up fix: see `OrientationGate.ts`'s own doc. No `requiresExplicitResume` - this Scene has no run/state to protect, so rotating back to portrait restores interaction immediately. */
  private orientationGate!: OrientationGateHandle;

  constructor() {
    super("ScoreChallengeScene");
  }

  create(data: { token?: string; qaPreset?: ScoreChallengeQaPresetId }): void {
    this.token = data.token ?? "";
    this.loadState = "loading";
    this.resolved = null;
    this.isDisposed = false;
    this.isCreatingChallenge = false;
    this.shareCreateError = "";
    // Decided once, up front - a QA fixture preview's share modal must
    // never fire a real `score_share_*` PostHog event (see
    // `scoreChallengeQa.ts`'s "never contaminate real funnel data"
    // guarantee), so the modal is constructed with `qaMode` already known,
    // not toggled after the fact.
    const isQaFixtureRequested = !!data.qaPreset && isScoreChallengeQaAllowedHost(window.location.hostname);
    this.isQaFixture = isQaFixtureRequested;
    this.cameras.main.setBackgroundColor(GameConfig.colors.background);
    this.mainContainer = this.add.container(0, 0);
    this.shareModal = createShareChallengeModal(this, 95, "reshare", { qaMode: isQaFixtureRequested });

    this.buildLayout();
    this.scale.on("resize", this.onResize, this);
    this.events.once("shutdown", () => this.teardown());
    this.events.once("destroy", () => this.teardown());
    this.orientationGate = attachOrientationGate(this);
    this.orientationGate.check();

    if (isQaFixtureRequested) {
      this.loadFromQaFixture(data.qaPreset!);
      return;
    }

    // "The active CTA must disable or refresh if the page remains open
    // across rollover" - a cheap, infrequent local check against the
    // already-fetched `expires_at` (no extra network call needed to
    // detect this).
    this.expiryCheckEvent = this.time.addEvent({ delay: 15000, loop: true, callback: () => this.checkRollover() });

    void this.loadChallenge();
  }

  /**
   * QA-only: renders one synthetic fixture state directly, with no
   * network call, no `associate_daily_challenge_source`, and no real
   * `score_challenge_opened`/etc. PostHog event - see
   * `scoreChallengeQa.ts`'s doc for the full "never contaminate ranked or
   * viral data" guarantee. Gated by the caller (`create()`) already having
   * checked `isScoreChallengeQaAllowedHost`, so this method itself never
   * re-derives the host.
   */
  private loadFromQaFixture(preset: ScoreChallengeQaPresetId): void {
    this.isQaFixture = true;
    const fixture = buildScoreChallengeQaFixture(preset, Date.now());

    if (fixture.status === "active" && fixture.resolved) {
      const r = fixture.resolved;
      this.resolved = {
        challengeId: "qa-preview",
        targetDailyPoints: r.targetDailyPoints,
        creatorDisplayName: r.creatorDisplayName,
        analyticsId: "qa-preview",
        expiresAtMs: r.expiresAtMs,
        attemptsRemaining: r.attemptsRemaining,
        hasResult: r.hasResult,
        dailyPoints: r.dailyPoints,
        comparison: r.dailyPoints !== null ? compareToTarget(r.dailyPoints, r.targetDailyPoints) : null,
      };
      this.loadState = "resolved";
    } else if (fixture.status === "expired") {
      this.loadState = "expired";
    } else if (fixture.status === "invalid") {
      this.loadState = "invalid";
    } else {
      this.loadState = "self";
    }
    this.buildLayout();
  }

  private teardown(): void {
    this.isDisposed = true;
    this.scale.off("resize", this.onResize, this);
    this.expiryCheckEvent?.remove();
    this.shareModal.destroy();
  }

  private onResize(): void {
    if (this.isDisposed) return;
    this.buildLayout();
    this.shareModal.relayout();
  }

  private checkRollover(): void {
    if (this.isDisposed || this.loadState !== "resolved") return;
    void this.loadChallenge();
  }

  // ---------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------

  /**
   * The entire body runs inside one try/catch - "unexpected exceptions
   * must all fail safely into a visible friendly state" is a BLANKET
   * guarantee at this level, not something chased dependency-by-dependency:
   * whatever throws (a lower-level client's own bug, a future change that
   * introduces a new failure mode, anything this function doesn't
   * specifically anticipate) always converges to the same `"unavailable"`
   * render rather than leaving the scene stuck on "LOADING CHALLENGE…"
   * forever with an unhandled rejection nobody sees.
   */
  private async loadChallenge(): Promise<void> {
    try {
      if (!isPlausibleChallengeToken(this.token)) {
        this.loadState = "invalid";
        this.buildLayout();
        this.trackOpenedOnce("invalid", false);
        return;
      }

      const result = await ScoreChallengeClient.getChallenge(this.token);
      if (this.isDisposed) return;

      const outcome = resolveScoreChallengeLookup(result);

      if (outcome.type === "unavailable") {
        // Never tracked as a real "opened" event - an infrastructure
        // failure (RPC unavailable, network rejection, malformed
        // response) is not a genuine visit to a real-or-fake link, and
        // must never inflate/pollute score_challenge_opened's counts.
        this.loadState = "unavailable";
        this.buildLayout();
        return;
      }
      if (outcome.type === "invalid") {
        this.loadState = "invalid";
        this.buildLayout();
        this.trackOpenedOnce("invalid", false);
        return;
      }
      if (outcome.type === "expired") {
        this.loadState = "expired";
        this.buildLayout();
        this.trackOpenedOnce("expired", false);
        return;
      }
      if (outcome.type === "self") {
        this.loadState = "self";
        this.buildLayout();
        this.trackOpenedOnce("active", true);
        return;
      }

      // outcome.type === "active": a real visitor on an active link.
      // Record verified attribution (silently a no-op server-side for a
      // self-referral/expired/unknown token - none of which apply here,
      // but the endpoint is safe to call regardless) and remember it as
      // this tab's attribution source for today, THEN inspect the
      // visitor's own existing Daily state.
      const view = outcome.view;
      ScoreChallengeSession.setActiveSource(view.challengeId, this.token);
      void ScoreChallengeClient.associateSource(this.token);

      const platform = detectPlatform();
      const [statusResult, rankResult] = await Promise.all([
        DailyChallengeClient.getMyStatus(view.challengeId, platform),
        DailyChallengeClient.getMyRank(view.challengeId, platform),
      ]);
      if (this.isDisposed) return;

      if (statusResult.state !== "ok" || !statusResult.status || rankResult.state !== "ok" || !rankResult.rank) {
        this.loadState = "unavailable";
        this.buildLayout();
        return;
      }

      const attemptsRemaining = Math.max(0, 3 - statusResult.status.attemptsReserved);
      const hasResult = rankResult.rank.hasResult && rankResult.rank.dailyPoints !== null;
      const dailyPoints = hasResult ? rankResult.rank.dailyPoints : null;

      this.resolved = {
        challengeId: view.challengeId,
        targetDailyPoints: view.targetDailyPoints,
        creatorDisplayName: view.creatorDisplayName ?? "ANONYMOUS",
        analyticsId: view.analyticsId ?? "",
        expiresAtMs: view.expiresAtMs,
        attemptsRemaining,
        hasResult,
        dailyPoints,
        comparison: dailyPoints !== null ? compareToTarget(dailyPoints, view.targetDailyPoints) : null,
      };
      this.loadState = "resolved";
      this.buildLayout();
      this.trackOpenedOnce("active", false);
    } catch {
      if (this.isDisposed) return;
      this.loadState = "unavailable";
      this.buildLayout();
    }
  }

  private trackOpenedOnce(status: "active" | "expired" | "invalid", selfReferral: boolean): void {
    if (ScoreChallengeSession.hasEventFired("score_challenge_opened", this.token)) return;
    ScoreChallengeSession.markEventFired("score_challenge_opened", this.token);
    trackScoreChallengeOpened({ status, selfReferral, returningPlayer: ScoreChallengeSession.isReturningPlayer() });
  }

  // ---------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------

  private beatThisScore(): void {
    // A QA fixture must never consume a real Daily attempt - see
    // `scoreChallengeQa.ts`'s doc. `isQaFixture` is checked FIRST, before
    // even looking at `this.resolved` (which is itself synthetic data in
    // this case).
    if (this.isQaFixture || !this.resolved || this.resolved.attemptsRemaining <= 0) return;
    trackScoreChallengeCtaClicked(attemptsRemainingBucket(this.resolved.attemptsRemaining));
    this.scene.start("DailyChallengeScene", {
      sourceChallenge: {
        publicId: this.token,
        targetDailyPoints: this.resolved.targetDailyPoints,
        creatorDisplayName: this.resolved.creatorDisplayName,
        analyticsId: this.resolved.analyticsId,
      },
    });
  }

  private playTodaysDaily(): void {
    this.scene.start("DailyChallengeScene");
  }

  /**
   * Creates/retrieves the challenge link and opens the in-game disclosure
   * modal (round-5 fix) - never itself invokes the OS share sheet or the
   * clipboard. A QA fixture (`isQaFixture`) skips the real network call
   * entirely and opens the SAME modal with synthetic fixture data instead
   * - the modal itself was constructed with `qaMode: true` in `create()`,
   * so any SHARE/COPY LINK tap inside it still exercises the real browser
   * APIs (useful for manually testing the actual OS share sheet/clipboard)
   * but fires no `score_share_*` PostHog event - see
   * `scoreChallengeQa.ts`'s doc. This is the "safe QA/manual way to
   * preview and exercise this share modal without creating another ranked
   * result" the round-5 spec asks for.
   */
  private async challengeAFriend(): Promise<void> {
    if (this.isCreatingChallenge || !this.resolved || this.resolved.dailyPoints === null || !isSharingEnabled()) return;

    if (this.isQaFixture) {
      const url = buildChallengeUrl(resolveChallengeBaseUrl(window.location.hostname, window.location.origin), "qa-preview-fixture");
      this.shareModal.open(this.resolved.creatorDisplayName, this.resolved.targetDailyPoints, url);
      return;
    }

    this.isCreatingChallenge = true;
    this.shareCreateError = "";
    this.buildLayout();

    const result = await ScoreChallengeClient.createOrGetChallenge(detectPlatform());
    this.isCreatingChallenge = false;
    if (this.isDisposed) return;
    if (result.state !== "ok" || !result.publicId) {
      this.shareCreateError = "Could not create your challenge link. Try again.";
      this.buildLayout();
      return;
    }
    this.buildLayout();

    const baseUrl = resolveChallengeBaseUrl(window.location.hostname, window.location.origin);
    const url = buildChallengeUrl(baseUrl, result.publicId);
    trackScoreSharePromptViewed("reshare");
    this.shareModal.open(result.creatorDisplayName ?? this.resolved.creatorDisplayName, result.targetDailyPoints ?? this.resolved.dailyPoints, url);
  }

  // ---------------------------------------------------------------------
  // Layout
  // ---------------------------------------------------------------------

  private buildLayout(): void {
    const { width, height } = this.scale;
    const centerX = width / 2;

    if (this.loadState !== "resolved") {
      this.mainContainer.removeAll(true);
      let y = height * 0.12;
      y = this.renderQaBadge(centerX, y, false);
      switch (this.loadState) {
        case "loading":
          this.renderMessage(centerX, y, "LOADING CHALLENGE…");
          break;
        case "unavailable":
        case "invalid":
          this.renderUnavailable(centerX, y, "CHALLENGE UNAVAILABLE", "But today's Daily is ready.");
          break;
        case "expired":
          this.renderUnavailable(centerX, y, "THIS CHALLENGE HAS ENDED", "A new Daily Challenge is live.");
          break;
        case "self":
          this.renderSelf(centerX, y);
          break;
      }
      return;
    }

    // The full "resolved" card (name/target/subtitle/ends-in/comparison/
    // attempts/buttons) can run long enough - a long display name or a
    // comparison banner both add wrapped lines - that its normal spacing
    // runs past the bottom of the Phaser canvas on a short viewport
    // (landscape phones, but also plain small portrait ones once a long
    // name is in play), and the canvas never scrolls (Scale.RESIZE fits
    // it exactly to the viewport). Rather than guess in advance from
    // viewport height alone whether THIS content needs the tighter
    // (compact) spacing/font set, build it once normally, measure the
    // real bottom it reached, and only redo it compact if that measured
    // bottom actually overflows - the same "measure, don't guess" rule
    // the rest of this fix follows, applied to picking compact vs normal.
    this.mainContainer.removeAll(true);
    let y = height * 0.12;
    y = this.renderQaBadge(centerX, y, false);
    const normalBottom = this.renderResolved(centerX, y, width, false);

    if (normalBottom > height - 12) {
      this.mainContainer.removeAll(true);
      let compactY = height * 0.02;
      compactY = this.renderQaBadge(centerX, compactY, true);
      this.renderResolved(centerX, compactY, width, true);
    }
  }

  /** Draws the "QA PREVIEW" badge (QA fixtures only) and returns the next free Y - shared by every `buildLayout()` branch so the compact/normal remeasure above doesn't duplicate it. */
  private renderQaBadge(centerX: number, y: number, compact: boolean): number {
    if (!this.isQaFixture) return y;
    const badge = this.add
      .text(centerX, y, "QA PREVIEW — VISUAL ONLY, NOT REAL DATA", {
        fontFamily: "Arial, sans-serif",
        fontSize: compact ? "10px" : "12px",
        fontStyle: "bold",
        color: "#0f1117",
        backgroundColor: "#ffd166",
        padding: compact ? { x: 6, y: 2 } : { x: 8, y: 4 },
      })
      .setOrigin(0.5);
    this.mainContainer.add(badge);
    return y + (compact ? 10 : 34);
  }

  private renderMessage(centerX: number, y: number, message: string): number {
    const text = this.add
      .text(centerX, y + 20, message, { fontFamily: "Arial, sans-serif", fontSize: "18px", color: "#8b93a7", align: "center" })
      .setOrigin(0.5, 0);
    this.mainContainer.add(text);
    return text.y + text.height + 30;
  }

  private renderUnavailable(centerX: number, y: number, title: string, subtitle: string): number {
    const titleText = this.add
      .text(centerX, y, title, { fontFamily: "Arial, sans-serif", fontSize: "28px", fontStyle: "bold", color: "#f5f7fa" })
      .setOrigin(0.5);
    this.mainContainer.add(titleText);
    y += 44;

    const subtitleText = this.add
      .text(centerX, y, subtitle, { fontFamily: "Arial, sans-serif", fontSize: "15px", color: "#8b93a7" })
      .setOrigin(0.5);
    this.mainContainer.add(subtitleText);
    y += 48;

    const playButton = createButton(this, centerX, y, "PLAY TODAY'S DAILY", () => this.playTodaysDaily(), { width: 260, height: 56 });
    this.mainContainer.add(playButton);
    y += 72;

    // PR27: this state (an invalid/expired Beat My Score link) has no
    // other way out - without this, a visitor who doesn't want to play
    // Daily is trapped with a single CTA. Navigation only, same
    // destination the "resolved" state's own MAIN MENU button already
    // uses below.
    const menuButton = createButton(this, centerX, y, "MAIN MENU", () => this.scene.start("MenuScene"), {
      width: 220,
      height: 46,
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });
    this.mainContainer.add(menuButton);
    return y + 60;
  }

  private renderSelf(centerX: number, y: number): number {
    const titleText = this.add
      .text(centerX, y, "THIS IS YOUR CHALLENGE", { fontFamily: "Arial, sans-serif", fontSize: "26px", fontStyle: "bold", color: "#f5f7fa" })
      .setOrigin(0.5);
    this.mainContainer.add(titleText);
    y += 44;

    const subtitleText = this.add
      .text(centerX, y, "Share this link with a friend to see how they compare.", {
        fontFamily: "Arial, sans-serif",
        fontSize: "14px",
        color: "#8b93a7",
        align: "center",
        wordWrap: { width: Math.min(420, this.scale.width - 40) },
      })
      .setOrigin(0.5, 0);
    this.mainContainer.add(subtitleText);
    y = subtitleText.y + subtitleText.height + 30;

    const playButton = createButton(this, centerX, y, "PLAY TODAY'S DAILY", () => this.playTodaysDaily(), { width: 260, height: 56 });
    this.mainContainer.add(playButton);
    y += 72;

    // PR27: same reasoning as renderUnavailable()'s own MAIN MENU addition
    // - the creator of a link opening their own challenge shouldn't be
    // limited to "play Daily" if that's not what they wanted either.
    const menuButton = createButton(this, centerX, y, "MAIN MENU", () => this.scene.start("MenuScene"), {
      width: 220,
      height: 46,
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });
    this.mainContainer.add(menuButton);
    return y + 60;
  }

  private renderResolved(centerX: number, y: number, width: number, isCompactHeight: boolean): number {
    const state = this.resolved!;
    const wrapWidth = Math.min(440, width - 40);
    const gap = (normal: number, compact: number) => (isCompactHeight ? compact : normal);

    const nameLine = this.add
      .text(centerX, y, `${truncateDisplayName(state.creatorDisplayName, 18).toUpperCase()} CHALLENGED YOU`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "22px",
        fontStyle: "bold",
        color: "#f5f7fa",
        align: "center",
        // useAdvancedWrap lets a long, space-free display name (still
        // within its own 18-char truncation) that's wider than wrapWidth
        // break across lines by character instead of overflowing the
        // viewport horizontally - basic wrapping only breaks on spaces,
        // so a single unbroken run of characters would otherwise render
        // past both edges of a narrow viewport uncut.
        wordWrap: { width: wrapWidth, useAdvancedWrap: true },
      })
      // Top-anchored (not full-center): a long name can wrap to 2+ lines,
      // and a center-anchored line would then extend upward above `y`,
      // into the QA-preview badge (or, on a short viewport, off the top
      // of the screen) instead of only growing downward as intended.
      .setOrigin(0.5, 0);
    this.mainContainer.add(nameLine);
    y = nameLine.y + nameLine.height + gap(24, 4);

    const targetLine = this.add
      .text(centerX, y, `${state.targetDailyPoints.toLocaleString("en-US")} DAILY POINTS`, {
        fontFamily: "Arial, sans-serif",
        fontSize: isCompactHeight ? "26px" : "32px",
        fontStyle: "bold",
        color: hexToCss(GameConfig.colors.star),
        align: "center",
        // A max-valid (100,000 - see DAILY_RESULT_BOUNDS.maxScore) target
        // on a narrow viewport doesn't fit "100,000 DAILY POINTS" on one
        // line - wordWrap lets it break at the space instead of
        // overflowing, and the measured `.setOrigin(0.5, 0)` flow below
        // accounts for the real (1- or 2-line) height either way, rather
        // than assuming a single-line height as the old fixed `y += 44` did.
        wordWrap: { width: wrapWidth },
      })
      .setOrigin(0.5, 0);
    this.mainContainer.add(targetLine);
    y = targetLine.y + targetLine.height + gap(20, 4);

    const subtitle = this.add
      .text(centerX, y, "Beat this score before the Daily resets.", {
        fontFamily: "Arial, sans-serif",
        fontSize: isCompactHeight ? "12px" : "14px",
        color: "#8b93a7",
      })
      .setOrigin(0.5, 0);
    this.mainContainer.add(subtitle);
    y = subtitle.y + subtitle.height + gap(12, 4);

    if (state.expiresAtMs !== null) {
      const endsIn = this.add
        .text(centerX, y, `ENDS IN ${formatEndsIn(state.expiresAtMs - Date.now())}`, {
          fontFamily: "Arial, sans-serif",
          fontSize: isCompactHeight ? "11px" : "13px",
          color: "#5b8cff",
        })
        .setOrigin(0.5, 0);
      this.mainContainer.add(endsIn);
      y = endsIn.y + endsIn.height + gap(12, 4);
    }

    y += gap(10, 0);
    if (state.comparison) {
      y = this.renderComparisonBanner(centerX, y, state.comparison, wrapWidth, isCompactHeight);
    }

    // Top-anchored measured flow from here on: `y` is always "next free Y",
    // text uses `.setOrigin(0.5, 0)` so `.y`/`.height` are its real top/height,
    // and each button (center-anchored by createButton()) is placed at
    // `y + buttonHeight / 2` so its top edge lands exactly at `y`. Avoids the
    // fixed-offset regression where a guessed gap didn't match this text's
    // actual rendered height, letting the button's top edge overlap it.
    const attemptsText = this.add
      .text(centerX, y, `Attempts remaining: ${state.attemptsRemaining} of 3`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "14px",
        fontStyle: "bold",
        color: state.attemptsRemaining > 0 ? "#7dffb3" : "#ff5f6d",
      })
      .setOrigin(0.5, 0);
    this.mainContainer.add(attemptsText);
    y = attemptsText.y + attemptsText.height + gap(14, 4);

    if (state.attemptsRemaining > 0) {
      const note = this.add
        .text(centerX, y, "Your existing Daily attempts apply.", {
          fontFamily: "Arial, sans-serif",
          fontSize: isCompactHeight ? "11px" : "12px",
          color: "#8b93a7",
        })
        .setOrigin(0.5, 0);
      this.mainContainer.add(note);
      y = note.y + note.height + gap(18, 6);

      // BEAT THIS SCORE keeps its full 56px height even in compact mode -
      // it's the primary CTA, and every other saving above (tighter gaps,
      // smaller secondary/font sizes) exists specifically so this button
      // and its tap target never have to shrink to make the card fit.
      const beatButtonHeight = 56;
      const beatButton = createButton(this, centerX, y + beatButtonHeight / 2, "BEAT THIS SCORE", () => this.beatThisScore(), { width: 260, height: beatButtonHeight });
      this.mainContainer.add(beatButton);
      y += beatButtonHeight + gap(16, 6);
    } else if (!state.hasResult) {
      const note = this.add
        .text(centerX, y, "You've used all 3 attempts today without a completed result.", {
          fontFamily: "Arial, sans-serif",
          fontSize: "13px",
          color: "#8b93a7",
          align: "center",
          wordWrap: { width: wrapWidth },
        })
        .setOrigin(0.5, 0);
      this.mainContainer.add(note);
      y = note.y + note.height + gap(20, 8);
    }

    y = this.renderShareAndMenuRow(centerX, y, wrapWidth, state, isCompactHeight);
    return y;
  }

  private renderComparisonBanner(centerX: number, y: number, comparison: ScoreComparisonResult, wrapWidth: number, isCompactHeight: boolean): number {
    const { headline, subline } = describeComparison(comparison, this.resolved!.creatorDisplayName);
    const color = comparison.outcome === "beat" ? "#7dffb3" : comparison.outcome === "tie" ? "#ffd166" : "#ff5f6d";
    const gap = (normal: number, compact: number) => (isCompactHeight ? compact : normal);

    const headlineText = this.add
      .text(centerX, y, headline, {
        fontFamily: "Arial, sans-serif",
        fontSize: isCompactHeight ? "17px" : "20px",
        fontStyle: "bold",
        color,
        align: "center",
        // Same long-unbroken-name overflow risk as nameLine above -
        // headline embeds the same truncated opponent name (see
        // describeComparison()).
        wordWrap: { width: wrapWidth, useAdvancedWrap: true },
      })
      .setOrigin(0.5, 0);
    this.mainContainer.add(headlineText);
    y = headlineText.y + headlineText.height + (subline ? gap(6, 2) : 0);

    if (subline) {
      const sublineText = this.add
        .text(centerX, y, subline, {
          fontFamily: "Arial, sans-serif",
          fontSize: isCompactHeight ? "12px" : "14px",
          color,
          align: "center",
          wordWrap: { width: wrapWidth },
        })
        .setOrigin(0.5, 0);
      this.mainContainer.add(sublineText);
      y = sublineText.y + sublineText.height;
    }

    return y + gap(20, 4);
  }

  private renderShareAndMenuRow(centerX: number, y: number, wrapWidth: number, state: ResolvedState, isCompactHeight: boolean): number {
    if (this.shareCreateError) {
      const feedback = this.add
        .text(centerX, y, this.shareCreateError, {
          fontFamily: "Arial, sans-serif",
          fontSize: "13px",
          color: "#ff5f6d",
          align: "center",
          wordWrap: { width: wrapWidth },
        })
        .setOrigin(0.5, 0);
      this.mainContainer.add(feedback);
      y = feedback.y + feedback.height + (isCompactHeight ? 8 : 14);
    }

    const rowWidth = Math.min(420, wrapWidth);
    // Both still comfortably clear the 44pt/48dp iOS/Android minimum tap
    // target - only BEAT THIS SCORE (the primary CTA) keeps its full 56px.
    const rowHeight = isCompactHeight ? 48 : 52;
    // layoutButtonRows() places a row's buttons centered on `startY` (it
    // treats it as the row's vertical center, not a top edge) - so `y`
    // (the measured top of free space here) must first pass through
    // contentFlowStartY() the same way every other button grid in this
    // codebase (EndlessResultScene/DailyResultScene/MenuScene) derives its
    // own startY, or the row's top half would land back above `y`, into
    // whatever content was just measured above it.
    // floor must stay >= rowHeight / 2 (compact rowHeight is 48, so half is
    // 24) - contentFlowStartY() places the row's CENTER, so a floor below
    // half the row's real height would put the row's own top edge back
    // above `y`, overlapping whatever content was just measured there.
    const rowStartY = isCompactHeight
      ? contentFlowStartY(y, this.scale.height, { floor: 30, cap: 38, factor: 0.03 })
      : contentFlowStartY(y, this.scale.height, { floor: 28, cap: 44, factor: 0.045 });
    const canShare = state.hasResult && isSharingEnabled();
    const buttons: Array<{} | null> = canShare ? [{}, {}] : [{}];
    const { placements, bottomY } = layoutButtonRows([buttons], { availableWidth: rowWidth, startY: rowStartY, rowHeight });
    const rowPlacements = placements[0];

    let idx = 0;
    if (canShare) {
      const sharePlacement = rowPlacements[idx++]!;
      const shareButton = createButton(
        this,
        centerX + sharePlacement.x,
        sharePlacement.y,
        this.isCreatingChallenge ? "…" : "CHALLENGE A FRIEND",
        () => void this.challengeAFriend(),
        { width: sharePlacement.width, height: rowHeight, fontSize: sharePlacement.isTwoColumn ? "14px" : "17px", disabled: this.isCreatingChallenge },
      );
      this.mainContainer.add(shareButton);
    }

    const leaderboardPlacement = rowPlacements[idx]!;
    const leaderboardButton = createButton(
      this,
      centerX + leaderboardPlacement.x,
      leaderboardPlacement.y,
      "DAILY LEADERBOARD",
      () => this.scene.start("DailyLeaderboardScene", { challengeId: state.challengeId }),
      { width: leaderboardPlacement.width, height: rowHeight, fontSize: leaderboardPlacement.isTwoColumn ? "14px" : "17px", fillColor: 0x1c2030, hoverColor: 0x262c40, textColor: "#f5f7fa" },
    );
    this.mainContainer.add(leaderboardButton);

    y = bottomY + (isCompactHeight ? 4 : 16);
    const menuButton = createButton(this, centerX, y, "MAIN MENU", () => this.scene.start("MenuScene"), {
      width: 200,
      height: 44,
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });
    this.mainContainer.add(menuButton);
    return y + 56;
  }
}
